import React, { useState, useEffect, useRef } from 'react';
import { loadModels, detectFace, getFaceApi } from '../utils/faceApi';

// Draws stylish corner brackets (better than a plain rectangle)
function drawCorners(ctx, box, color = '#4ade80') {
  const { x, y, width: w, height: h } = box;
  const len = Math.min(w, h) * 0.2;
  ctx.strokeStyle = color;
  ctx.lineWidth   = 2.5;
  ctx.shadowColor = color;
  ctx.shadowBlur  = 8;
  ctx.lineCap     = 'round';
  [
    [[x, y + len], [x, y], [x + len, y]],
    [[x + w - len, y], [x + w, y], [x + w, y + len]],
    [[x, y + h - len], [x, y + h], [x + len, y + h]],
    [[x + w - len, y + h], [x + w, y + h], [x + w, y + h - len]],
  ].forEach(pts => {
    ctx.beginPath();
    ctx.moveTo(...pts[0]);
    ctx.lineTo(...pts[1]);
    ctx.lineTo(...pts[2]);
    ctx.stroke();
  });
  ctx.shadowBlur = 0;
}

export default function FaceCapture({ onCapture, onCancel, title = 'Face Scan', subtitle }) {
  const videoRef    = useRef(null);
  const canvasRef   = useRef(null);
  const streamRef   = useRef(null);
  const intervalRef = useRef(null);
  const lastResult  = useRef(null);

  const [phase, setPhase]   = useState('loading'); // loading | scanning | detected | capturing | error | denied
  const [errMsg, setErrMsg] = useState('');

  useEffect(() => {
    let alive = true;

    (async () => {
      try {
        await loadModels();
        if (!alive) return;

        const stream = await navigator.mediaDevices.getUserMedia({
          video: { facingMode: 'user', width: { ideal: 640 }, height: { ideal: 480 } },
          audio: false,
        });
        if (!alive) { stream.getTracks().forEach(t => t.stop()); return; }

        streamRef.current = stream;
        const video = videoRef.current;
        if (video) {
          video.srcObject = stream;
          await new Promise(res => { video.onloadedmetadata = res; });
          video.play();
        }

        if (!alive) return;
        setPhase('scanning');

        intervalRef.current = setInterval(async () => {
          const video  = videoRef.current;
          const canvas = canvasRef.current;
          if (!video || !canvas || video.readyState < 2) return;

          const result = await detectFace(video);
          lastResult.current = result || null;

          const displaySize = { width: video.offsetWidth, height: video.offsetHeight };
          if (!displaySize.width || !displaySize.height) return;
          getFaceApi().matchDimensions(canvas, displaySize);
          const ctx = canvas.getContext('2d');
          ctx.clearRect(0, 0, canvas.width, canvas.height);

          if (result) {
            const resized = getFaceApi().resizeResults(result, displaySize);
            drawCorners(ctx, resized.detection.box);
            setPhase('detected');
          } else {
            setPhase(p => p === 'detected' ? 'scanning' : p);
          }
        }, 400);

      } catch (err) {
        if (!alive) return;
        if (err?.name === 'NotAllowedError' || err?.name === 'PermissionDeniedError') {
          setErrMsg('Camera access denied. On iPhone: Settings → Privacy & Security → Camera → enable for this browser.');
          setPhase('denied');
        } else if (err?.name === 'NotFoundError' || err?.name === 'DevicesNotFoundError') {
          setErrMsg('No camera found on this device.');
          setPhase('error');
        } else {
          setErrMsg('Camera not available. Make sure you\'re on HTTPS and camera permissions are enabled.');
          setPhase('error');
        }
      }
    })();

    return () => {
      alive = false;
      clearInterval(intervalRef.current);
      streamRef.current?.getTracks().forEach(t => t.stop());
    };
  }, []);

  const handleCapture = () => {
    if (!lastResult.current) return;
    setPhase('capturing');
    clearInterval(intervalRef.current);
    streamRef.current?.getTracks().forEach(t => t.stop());
    onCapture(Array.from(lastResult.current.descriptor));
  };

  const STATUS = {
    loading:   { text: 'Loading face recognition models…',  color: 'var(--text3)', icon: '⏳' },
    scanning:  { text: 'Position your face in the frame…',  color: 'var(--text3)', icon: '👁' },
    detected:  { text: 'Face detected — ready to capture!', color: '#4ade80',      icon: '✅' },
    capturing: { text: 'Capturing…',                        color: 'var(--accent)', icon: '📸' },
    error:     { text: errMsg || 'Camera not available',    color: '#ef4444',       icon: '❌' },
    denied:    { text: errMsg || 'Camera access denied',    color: '#f97316',       icon: '🔒' },
  };
  const s = STATUS[phase] ?? STATUS.error;

  return (
    <div style={{
      position: 'fixed', inset: 0, zIndex: 3000,
      background: 'rgba(5,5,12,0.96)', backdropFilter: 'blur(20px)',
      display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '20px',
    }}>
      <div style={{
        width: '100%', maxWidth: '360px',
        background: 'rgba(16,16,28,0.98)',
        border: '1px solid var(--border)',
        borderRadius: '20px', padding: '24px',
        boxShadow: '0 24px 64px rgba(0,0,0,0.7)',
        animation: 'faceScanIn 0.3s cubic-bezier(0.34,1.56,0.64,1)',
      }}>
        <h3 style={{ fontFamily: 'var(--font-display)', fontSize: '1.15rem', marginBottom: '4px' }}>
          {title}
        </h3>
        {subtitle && (
          <p style={{ color: 'var(--text3)', fontSize: '0.78rem', marginBottom: '14px', lineHeight: 1.5 }}>
            {subtitle}
          </p>
        )}

        {/* Camera frame */}
        <div style={{
          position: 'relative', width: '100%', paddingBottom: '75%',
          borderRadius: '14px', overflow: 'hidden', background: '#000',
          marginBottom: '14px',
          border: `2px solid ${phase === 'detected' ? '#4ade80' : phase === 'denied' ? '#f97316' : 'rgba(255,255,255,0.08)'}`,
          boxShadow: phase === 'detected' ? '0 0 24px rgba(74,222,128,0.25)' : phase === 'denied' ? '0 0 16px rgba(249,115,22,0.2)' : 'none',
          transition: 'border-color 0.35s, box-shadow 0.35s',
        }}>
          <video
            ref={videoRef}
            autoPlay playsInline muted
            style={{ position: 'absolute', inset: 0, width: '100%', height: '100%', objectFit: 'cover' }}
          />
          <canvas
            ref={canvasRef}
            style={{ position: 'absolute', inset: 0, width: '100%', height: '100%' }}
          />

          {/* Face guide oval */}
          {(phase === 'scanning' || phase === 'loading') && (
            <div style={{
              position: 'absolute', inset: 0,
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              pointerEvents: 'none',
            }}>
              <div style={{
                width: '52%', height: '70%',
                border: '2px dashed rgba(255,255,255,0.2)',
                borderRadius: '50%',
              }} />
            </div>
          )}

          {/* Overlay for loading / error / denied */}
          {(phase === 'loading' || phase === 'error' || phase === 'denied') && (
            <div style={{
              position: 'absolute', inset: 0,
              background: 'rgba(0,0,0,0.75)',
              display: 'flex', flexDirection: 'column',
              alignItems: 'center', justifyContent: 'center', gap: '10px',
            }}>
              <span style={{ fontSize: '2rem' }}>{s.icon}</span>
              <p style={{ color: '#fff', fontSize: '0.75rem', textAlign: 'center', padding: '0 20px', lineHeight: 1.5 }}>
                {s.text}
              </p>
            </div>
          )}
        </div>

        {/* Status pill */}
        <div style={{
          textAlign: 'center', marginBottom: '16px', padding: '8px 12px',
          borderRadius: '8px',
          background: phase === 'detected' ? 'rgba(74,222,128,0.1)' : 'rgba(255,255,255,0.04)',
          transition: 'background 0.3s',
        }}>
          <p style={{ fontSize: '0.8rem', color: s.color }}>
            {s.icon} {s.text}
          </p>
        </div>

        <div style={{ display: 'flex', gap: '10px' }}>
          <button
            className="btn-ghost"
            onClick={onCancel}
            style={{ flex: 1 }}
          >Cancel</button>
          <button
            className="btn-primary"
            onClick={handleCapture}
            disabled={phase !== 'detected'}
            style={{
              flex: 2,
              opacity: phase === 'detected' ? 1 : 0.35,
              transition: 'opacity 0.3s',
            }}
          >
            {phase === 'capturing' ? '📸 Capturing…' : '📸 Capture'}
          </button>
        </div>
      </div>

      <style>{`
        @keyframes faceScanIn {
          from { opacity: 0; transform: scale(0.9) translateY(12px); }
          to   { opacity: 1; transform: scale(1) translateY(0); }
        }
      `}</style>
    </div>
  );
}
